import {
  Cpu,
  Gauge,
  HardDrive,
  MemoryStick,
  Phone,
  Server as ServerIcon,
  WifiOff,
} from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import {
  barColor,
  dotColorBySeverity,
  getMetricSeverity,
  getGroup,
} from '../helpers';
import type { ServerStatsRow } from '../types';

interface Props {
  server: ServerStatsRow;
}

/** Single server tile — metric bars + status dot + region-aware footer. */
export function ServerCard({ server }: Props) {
  const severity = getMetricSeverity(server);
  const group = getGroup(server.serverName);
  const isOffline = severity === 'offline';

  const memTotal = server.memTotal || 0;
  const memUsed = server.memUsed || 0;
  const diskTotal = server.diskTotal || 0;
  const diskUsed = server.diskUsed || 0;
  const cpuPct = Math.min(server.cpuUsed || 0, 100);
  const cores = server.cpuCores && server.cpuCores > 0 ? server.cpuCores : 1;
  const loadPct = Math.min(((server.load1m || 0) / cores) * 100, 100);
  const memPct = memTotal > 0 ? Math.min((memUsed / memTotal) * 100, 100) : 0;
  const diskPct =
    diskTotal > 0 ? Math.min((diskUsed / diskTotal) * 100, 100) : 0;

  return (
    <div
      className={cn(
        'flex h-full flex-col overflow-hidden rounded-lg border bg-card shadow-sm transition-shadow hover:shadow-md',
        severity === 'critical' &&
          'border-l-4 border-l-rose-500 border-rose-200',
        severity === 'warning' &&
          'border-l-4 border-l-amber-500 border-amber-200',
        severity === 'online' && 'border-emerald-200',
        severity === 'offline' && 'border-slate-200 bg-muted/30',
      )}
    >
      <div className="flex items-center gap-2 border-b border-border bg-muted/30 px-3 py-2">
        <ServerIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <div
            className={cn(
              'truncate text-sm font-semibold',
              isOffline ? 'text-muted-foreground' : 'text-foreground',
            )}
            title={server.serverName}
          >
            {server.serverName}
          </div>
          {server.ip && (
            <div className="truncate font-mono text-[10px] text-muted-foreground">
              {server.ip}
            </div>
          )}
        </div>
        <span
          className={cn(
            'flex h-3 w-3 items-center justify-center rounded-full text-xs leading-none',
            severity === 'critical' && 'animate-pulse',
          )}
          style={{ color: dotColorBySeverity(severity) }}
          title={severity}
        >
          ●
        </span>
      </div>

      {isOffline ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-1 py-6 text-muted-foreground">
          <WifiOff className="h-5 w-5" />
          <span className="text-xs font-semibold uppercase">Unreachable</span>
        </div>
      ) : (
        <div className="flex flex-1 flex-col gap-1.5 px-3 py-2 text-xs">
          <MetricRow
            icon={Cpu}
            label="CPU"
            pct={cpuPct}
            valueLabel={`${cpuPct.toFixed(0)}%`}
          />
          <MetricRow
            icon={MemoryStick}
            label="RAM"
            pct={memPct}
            valueLabel={`${memPct.toFixed(0)}%`}
            sub={`${memUsed.toFixed(1)} / ${memTotal.toFixed(1)} GB`}
          />
          <MetricRow
            icon={HardDrive}
            label="DISK"
            pct={diskPct}
            valueLabel={`${diskPct.toFixed(0)}%`}
            sub={`${diskUsed.toFixed(0)} / ${diskTotal.toFixed(0)} GB`}
          />
          <MetricRow
            icon={Gauge}
            label="LOAD"
            pct={loadPct}
            valueLabel={`${(server.load1m ?? 0).toFixed(2)}`}
            sub={`5m ${(server.load5m ?? 0).toFixed(2)}  ·  15m ${(server.load15m ?? 0).toFixed(2)}  ·  ${cores} cores`}
          />
        </div>
      )}

      {!isOffline && group !== 'db' && (
        <div className="flex items-center justify-between border-t border-border bg-muted/20 px-3 py-1.5">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Calls
          </span>
          <span
            className={cn(
              'inline-flex items-center gap-1 font-mono text-xs font-semibold',
              (server.activeCalls ?? 0) > 0
                ? 'text-emerald-700'
                : 'text-muted-foreground',
            )}
          >
            <Phone className="h-3 w-3" />
            {(server.activeCalls ?? 0).toLocaleString()}
          </span>
        </div>
      )}
    </div>
  );
}

interface MetricRowProps {
  icon: typeof Cpu;
  label: string;
  pct: number;
  valueLabel: string;
  sub?: string;
}

function MetricRow({ icon: Icon, label, pct, valueLabel, sub }: MetricRowProps) {
  return (
    <div>
      <div className="flex items-center gap-2">
        <Icon className="h-3 w-3 shrink-0 text-muted-foreground" />
        <span className="w-9 shrink-0 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
        <div className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
          <div
            className="absolute inset-y-0 left-0 transition-[width]"
            style={{
              width: `${pct}%`,
              background: barColor(pct),
            }}
          />
        </div>
        <span
          className="w-10 shrink-0 text-right text-[10px] font-mono font-semibold tabular-nums"
          style={{ color: barColor(pct) }}
        >
          {valueLabel}
        </span>
      </div>
      {sub && (
        <div className="pl-12 text-[9.5px] text-muted-foreground">{sub}</div>
      )}
    </div>
  );
}
